import React, { useEffect, useState } from 'react';
import { ResellerTaxExemptType } from '@/types/gql-op-types';
import { isOptionalUrl } from '@/lib/utils';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { RescueAccountView, contactInfoFromView, toContactInfoInput } from '../../lib/accountAdapters';
import { useOrganizationSave } from './useAccountSave';
import styles from './Account.module.scss';

// Same constraint the legacy /vet/account screen enforces.
const NAME_REGEX = /^[\p{L}\p{N}]+[\p{L}\p{N} ,.'-]*$/u;
const NAME_ERROR = `Name must start with a letter or number and contain only letters, numbers, spaces, or the following characters: ,.'-`;

/** ToggleGroup needs a string value, so null taxExemptType maps to this sentinel. */
const ORG_TYPE_NONE = 'NONE';

const ORG_TYPE_OPTIONS = [
  { value: ResellerTaxExemptType.REGISTERED_501C3, label: 'Registered 501c3' },
  { value: ResellerTaxExemptType.GOVERNMENT_ENTITY, label: 'Municipal / Government Shelter' },
  { value: ORG_TYPE_NONE, label: 'Neither' },
];

interface EditOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  view: RescueAccountView;
}

export default function EditOrganizationModal({ open, onClose, view }: EditOrganizationModalProps) {
  const { save, saving } = useOrganizationSave();

  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [orgType, setOrgType] = useState<string>(ORG_TYPE_NONE);
  const [showErrors, setShowErrors] = useState(false);

  // Re-seed from the current view each time the modal opens, so a cancelled
  // edit doesn't leak into the next one.
  useEffect(() => {
    if (open) {
      setName(view.organization.name);
      setWebsite(view.organization.website);
      setOrgType(view.organization.taxExemptType ?? ORG_TYPE_NONE);
      setShowErrors(false);
    }
  }, [open, view]);

  const trimmedName = name.trim();
  const nameError = !trimmedName ? 'Please enter a name' : !NAME_REGEX.test(trimmedName) ? NAME_ERROR : undefined;
  const websiteError = isOptionalUrl(website.trim()) ? undefined : 'Please enter a valid website';

  async function handleSave() {
    if (nameError || websiteError) {
      setShowErrors(true);
      return;
    }
    const ok = await save(
      {
        resellerInput: view.resellerId
          ? {
              id: view.resellerId,
              name: trimmedName,
              taxExemptType: orgType === ORG_TYPE_NONE ? null : (orgType as ResellerTaxExemptType),
              contactInfo: toContactInfoInput({ ...contactInfoFromView(view), website: website.trim() }),
            }
          : null,
        householdId: view.householdId,
        name: trimmedName,
      },
      'Organization updated',
    );
    if (ok) {
      onClose();
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>Edit Organization</ModalTitle>
      <p className={styles.modalDesc}>
        Update how your rescue appears across Fi. Changes apply to adoption registrations, postcards, and your
        dashboard.
      </p>

      <PortalField label="Organization name" error={nameError} showError={showErrors && !!nameError}>
        <PortalInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your rescue's name"
          hasError={showErrors && !!nameError}
        />
      </PortalField>

      <PortalField label="Organization email" hint="This is your login email and can't be changed here.">
        <PortalInput value={view.organization.email} disabled />
      </PortalField>

      {view.isReseller && (
        <PortalField label="Website (optional)" error={websiteError} showError={showErrors && !!websiteError}>
          <PortalInput
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="www.yourrescue.org"
            hasError={showErrors && !!websiteError}
          />
        </PortalField>
      )}

      {view.isReseller && (
        <PortalField label="Organization type">
          <ToggleGroup variant="segmented" options={ORG_TYPE_OPTIONS} value={orgType} onChange={setOrgType} />
        </PortalField>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
